import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Send, CheckCircle, Calendar, Users, DollarSign, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteRequest = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quoteData, setQuoteData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventTime: '',
    venue: '',
    message: ''
  });

  useEffect(() => {
    const stored = sessionStorage.getItem('quoteData');
    if (stored) {
      setQuoteData(JSON.parse(stored));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Submit to API
      const response = await axios.post(`${API}/api/quote-requests`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        venue: formData.venue,
        event_date: formData.eventDate,
        event_time: formData.eventTime,
        message: formData.message,
        guest_count: quoteData?.guestCount || 50,
        event_type: quoteData?.eventType,
        selected_menus: quoteData?.selectedMenus || [],
        selected_bar: quoteData?.selectedBar,
        selected_late_night: quoteData?.selectedLateNight || [],
        selected_addons: quoteData?.selectedAddons || [],
        selected_rentals: quoteData?.selectedRentals || [],
        selected_plated_mains: quoteData?.selectedPlatedMains || [],
        include_gratuity: false,
        totals: quoteData?.totals
      });
      
      setQuoteNumber(response.data.quote_number);
      setSubmitted(true);
      toast({
        title: 'Quote Request Sent!',
        description: `Your quote number is ${response.data.quote_number}. We will contact you within 24 hours.`
      });
      
      // Clear session storage
      sessionStorage.removeItem('quoteData');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit quote request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f5dc] to-[#e8dcc8] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-none shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Quote Request Received!</h2>
            {quoteNumber && (
              <p className="text-lg font-medium text-[#8b7355] mb-2">
                Quote #: {quoteNumber}
              </p>
            )}
            <p className="text-gray-600 mb-6">
              Thank you for your interest! Our catering team will review your request and contact you within 24 hours.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/quote')}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
              >
                Create Another Quote
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f5dc] to-[#e8dcc8]">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/quote')}
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Calculator
          </Button>
          <h1 className="text-2xl font-bold">Request Your Quote</h1>
          <p className="text-gray-200">Fill in your details and we&apos;ll get back to you shortly</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Quote Summary */}
          {quoteData && (
            <div className="md:col-span-1">
              <Card className="border-none shadow-lg sticky top-4">
                <CardHeader className="bg-[#8b7355] text-white rounded-t-lg">
                  <CardTitle className="text-lg">Quote Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <Users size={18} className="text-[#1e3a5f]" />
                    <span>{quoteData.guestCount} Guests</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar size={18} className="text-[#1e3a5f]" />
                    <span className="capitalize">{quoteData.eventType?.replace('_', ' ')} Event</span>
                  </div>
                  
                  {quoteData.selectedMenus?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">MENUS</p>
                      {quoteData.selectedMenus.map(m => (
                        <p key={m.id} className="text-sm">{m.name}</p>
                      ))}
                    </div>
                  )}
                  
                  {quoteData.selectedBar && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">BAR</p>
                      <p className="text-sm">{quoteData.selectedBar.name}</p>
                    </div>
                  )}
                  
                  {quoteData.selectedAddons?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">ADD-ONS</p>
                      <p className="text-sm">{quoteData.selectedAddons.length} items selected</p>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign size={20} className="text-[#1e3a5f]" />
                        <span className="font-semibold">Estimated Total</span>
                      </div>
                      <span className="text-xl font-bold text-[#1e3a5f]">
                        ${quoteData.totals?.total?.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      ${(quoteData.totals?.total / quoteData.guestCount)?.toFixed(2)}/person
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contact Form */}
          <div className={quoteData ? 'md:col-span-2' : 'md:col-span-3'}>
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-[#1e3a5f] text-white rounded-t-lg">
                <CardTitle>Your Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Smith"
                        className="mt-1"
                        data-testid="quote-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-1"
                        data-testid="quote-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                        data-testid="quote-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="venue">Event Venue</Label>
                      <Input
                        id="venue"
                        value={formData.venue}
                        onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                        placeholder="Venue name or address"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="eventDate">Preferred Event Date</Label>
                      <Input
                        id="eventDate"
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                        className="mt-1"
                        data-testid="quote-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="eventTime">Preferred Start Time</Label>
                      <Input
                        id="eventTime"
                        type="time"
                        value={formData.eventTime}
                        onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Details or Questions</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your event, dietary requirements, or any questions you have..."
                      rows={4}
                      className="mt-1"
                      data-testid="quote-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold py-6 text-lg disabled:opacity-50"
                    data-testid="submit-quote-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" />
                        Submit Quote Request
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to be contacted by our catering team.
                    We typically respond within 24 hours.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1e3a5f] text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-300">
            Willow Ridge Catering • Questions? Call us at (555) 123-4567
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteRequest;
